import { Skeleton } from '@/components/ui/skeleton'
import { SectionLabel } from '@/components/custom/section-label'
import { cn } from '@/lib/utils'

// Title/subtitle widths — literal strings so Tailwind's scanner keeps the classes; varied so
// the list reads like real content rather than a uniform grid.
const ROW_WIDTHS = [
  { key: 0, title: 'w-2/5', sub: 'w-1/4' },
  { key: 1, title: 'w-1/3', sub: 'w-1/5' },
  { key: 2, title: 'w-1/2', sub: 'w-1/4' },
  { key: 3, title: 'w-2/5', sub: 'w-1/3' },
  { key: 4, title: 'w-1/3', sub: 'w-1/5' },
  { key: 5, title: 'w-1/2', sub: 'w-1/4' },
]

function ListRowSkeleton(props: { title: string; sub: string; collapsed?: boolean; tallAvatar?: boolean }) {
  return (
    <div
      data-slot="list-row-skeleton"
      className={cn(
        // Stands in for an `Item`, whose transparent 1px border is part of its height (item.tsx). Squashed it
        // KEEPS that border and holds `p-px` — the 2px ring the real row wears around the avatar in the rail
        // (collapsible.tsx), so the row there is 36px and not the avatar's own 32.
        'flex items-center rounded-lg border border-transparent [transition-property:padding,gap] duration-(--squash) ease-in-out',
        props.collapsed ? 'mx-auto w-fit justify-center gap-0 p-px' : 'gap-2 px-2.5 py-2',
      )}
    >
      {/* Empleados' rows carry the `lg` avatar, which is TALLER than their two type boxes and so sets the row's
          height on its own; every rail's row carries `md`, where the two lines set it. */}
      <Skeleton className={cn('shrink-0 rounded-md', props.tallAvatar ? 'size-9' : 'size-8')} />
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col overflow-hidden [transition-property:opacity,max-height] ease-in-out',
          props.collapsed
            ? 'max-h-0 opacity-0 [transition-duration:var(--squash-fast),var(--squash)]'
            : 'max-h-16 opacity-100 duration-(--squash)',
        )}
      >
        {/* The row's two type boxes, not two spacing steps: the real title and subtitle are what set its height. */}
        <div className="text-sm leading-snug">
          <Skeleton className={`inline-block h-3.5 align-middle ${props.title}`} />
        </div>
        <div className="text-xs">
          <Skeleton className={`inline-block h-3 align-middle ${props.sub}`} />
        </div>
      </div>
    </div>
  )
}

/**
 * ScrollableList's fetching bar, which draws over the list's first pixel and pulls the rows back up through its
 * own gap (`-top-3 -mt-3 mb-1` there). Without it every list skeleton starts its rows 3px too low.
 */
export function FetchingBarSkeleton() {
  return <div className="-mt-3 mb-1 border-t border-transparent" />
}

export function ListSkeleton(props: { rows?: number; collapsed?: boolean; showLabel?: boolean; tallAvatar?: boolean }) {
  const rows = props.rows ?? 6
  // The count label only leads the rows where no strip carries it (shared/score-controls.tsx). Squashed it keeps
  // its padding box — `min-h-0` empties the content, not the padding — so the rail reserves 12px for it too.
  const showLabel = props.showLabel ?? true
  return (
    <>
      {showLabel && (
        <div
          className={cn(
            'grid [transition-property:grid-template-rows,opacity] duration-(--squash) ease-in-out',
            props.collapsed ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100',
          )}
        >
          {/* The real label's own box, with the bar as its inline content: the two cannot drift apart. */}
          <SectionLabel className="block min-h-0 overflow-hidden px-2">
            <Skeleton className="inline-block h-2.5 w-14" />
          </SectionLabel>
        </div>
      )}
      {ROW_WIDTHS.slice(0, rows).map((w) => (
        <ListRowSkeleton
          key={w.key}
          title={w.title}
          sub={w.sub}
          collapsed={props.collapsed}
          tallAvatar={props.tallAvatar}
        />
      ))}
    </>
  )
}
